import React from "react";
import { Text, plainText } from "../../atoms/text/text";
import { ChannelIdentity } from "./channel-identity";
import type { LineupColumn, LineupRow } from "./channel-lineup.types";
import { Disclosure } from "./disclosure";
import { Indicator } from "./indicator";
import { indicatorAccessibleName } from "./indicator.shared";
import { LocalityBadge, localityKindFor } from "./locality-badge";
import type { LineupRowCopy } from "./row-copy.shared";
import { ShowMoreToggle } from "./show-more-toggle";
import styles from "./channel-row.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * One channel row of the Signature packages grid — GR1-GR5, GR7, GR8, GR9. The identity cell on the
 * left, then one cell per column, then an RSN's See-details disclosure across the full width.
 */

/** Shared so a memoised row with no copy compares one stable reference. */
const NO_COPY: LineupRowCopy = {};

export interface ChannelRowProps {
  /** RESOLVER-FED (camelCase). The channel and its positional cells. */
  row: LineupRow;
  /**
   * Resolver-fed (camelCase). The grid's columns, in authored order. Read for each cell's package
   * label (GR2, GR3, AX3) and for the track count — never re-ordered, never filtered.
   */
  columns: LineupColumn[];
  /** GR5 — the legal note for an RSN's tier, keyed by tier number. */
  tierLegalNotes?: Record<number, string>;
  /**
   * GR5 — the legal note an RSN falls back to when its tier has no entry in `tierLegalNotes`, or
   * when it carries no tier at all.
   */
  defaultLegalNote?: string;
  /**
   * AX6 — reveals each row's channel logo as a tooltip trigger. Off by default;
   * `channel-identity.tsx` carries the argument for that default.
   */
  showLogoTooltip?: boolean;
  /**
   * A short line under the row, drawn as a strip across its whole width. Absent or blank draws
   * nothing, which is every browse-mode row. It is GQ4's stripe in words, and that is what it is
   * for.
   */
  note?: string;
  /** The authored wording the row draws: badges, cell names, tooltips, See details. */
  rowCopy?: LineupRowCopy;
  /** Layout only. */
  className?: string;
}

/** GR5 — "the legal note for its tier", then the default. */
function rsnLegalNote(
  tier: number | undefined,
  tierLegalNotes?: Record<number, string>,
  defaultLegalNote?: string
): string {
  const tierNote = typeof tier === "number" ? tierLegalNotes?.[tier] : undefined;
  return plainText((tierNote ?? "").trim() || (defaultLegalNote ?? "").trim());
}

/** GR5's details panel — the teams lines and the tier's legal note. */
/* The tier footnote repeats on the legal line only when that line IS the
   tier’s own note and the name carries the same superscript (tier > 1). */
function tierNoteMarker(tier: number | undefined, tierLegalNotes?: Record<number, string>): number | undefined {
  return typeof tier === "number" && tier > 1 && (tierLegalNotes?.[tier] ?? "").trim() !== "" ? tier : undefined;
}

function ChannelRowDetails({
  teams,
  legalNote,
  legalTier,
  rowCopy
}: {
  teams: string[];
  legalNote?: string;
  /** The tier the note belongs to; drawn as its footnote marker (`16174:27986`). */
  legalTier?: number;
  rowCopy: LineupRowCopy;
}) {
  const teamsLabel = plainText((rowCopy.teamsLabel ?? "").trim());
  return (
    <Disclosure
      className={styles.details}
      hideChevron
      summary={
        <ShowMoreToggle
          className={styles.detailsToggle}
          lessLabel={plainText((rowCopy.detailsLessLabel ?? "").trim())}
          moreLabel={plainText((rowCopy.detailsMoreLabel ?? "").trim())}
        />
      }
      summaryClassName={styles.detailsSummary}
    >
      <div className={styles.detailsBody}>
        {teams.length > 0 && (
          /* GR5 — one strip, "Teams:" then the names inline, as the RSN row
             frame draws it (`16174:27981`). Prose, not a `<ul>`: a list
             announcement would wrap a two-word label. */
          <p className={styles.detailsTeams}>
            {teamsLabel !== "" && (
              <>
                <Text as="span" className={styles.detailsLine} variant="body-3" weight="medium">
                  {teamsLabel}
                </Text>{" "}
              </>
            )}
            <Text as="span" className={styles.detailsLine} variant="body-3">
              {teams.join(", ")}
            </Text>
          </p>
        )}
        {legalNote && (
          <Text as="p" className={styles.detailsLegal} variant="label">
            {legalTier !== undefined && <sup className={styles.detailsTier}>{legalTier}</sup>}
            {legalNote}
          </Text>
        )}
      </div>
    </Disclosure>
  );
}

function ChannelRowImpl({
  row,
  columns,
  tierLegalNotes,
  defaultLegalNote,
  showLogoTooltip,
  note,
  rowCopy = NO_COPY,
  className
}: ChannelRowProps) {
  const { channel } = row;
  /* GR4 — which badge this channel wears, decided once by the shared leaf so the row, the pack grid
     and the search results cannot disagree. */
  const localityKind = localityKindFor(channel);

  /* GR5 / GR9 — team names are plain text like every other label; the live
     feed ships markup inside one ("Telemundo East<br/>"). An entry that
     reduces to nothing is dropped rather than drawn as an empty list item. */
  const teams = (channel.teams ?? []).map(plainText).filter(Boolean);
  /* GR5 — "each RSN row has a See details toggle", unconditionally: the AC owner asked for the
     toggle on every RSN row, so the content gate that used to sit here is gone. */
  const showDetails = channel.kind === "rsn";
  /* GR9 — the same reduction every other label on this row gets; a marker authored with markup in
     it must not render as tags either. Blank falls through to nothing, exactly as
     `subscription-row.tsx` treats its own note. */
  const noteText = plainText(note ?? "");

  return (
    <div
      className={[
        styles.row,
        (showDetails || noteText !== "") && styles.rowTall,
        showDetails && styles.rowDetails,
        className
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* GR1 — logo, name and number stacked and centred; GR5's superscript
          tier and GR4's badge are placed by the same leaf. GR8's "no logo"
          fallback rides inside it on `channel-logo.tsx`. */}
      <ChannelIdentity
        badge={localityKind ?
          (
            <LocalityBadge
              kind={localityKind}
              label={rowCopy.badgeLabels?.[localityKind]}
              tooltip={rowCopy.badgeTooltips?.[localityKind]}
            />
          ) :
          undefined}
        className={styles.identityCell}
        logoUrl={channel.logoUrl}
        name={channel.name}
        number={channel.number}
        showLogoTooltip={showLogoTooltip}
        tier={channel.tier}
      />

      {columns.map((column, index) => {
        const membership = row.cells[index] ?? null;

        return (
          <div className={styles.cell} key={column.billingProductCode}>
            {membership ?
              <Indicator
                channelName={channel.name}
                membership={membership}
                packageLabel={column.label}
                stateLabels={rowCopy.stateLabels}
                tooltipPrefix={rowCopy.tooltipPrefixes?.[membership]}
              /> :
              /* AX3 — the empty cell's name. See the header: there is no
                 badge here to carry it. */
              <span className="dds-sr-only">
                {indicatorAccessibleName(channel.name, column.label, null, rowCopy.stateLabels)}
              </span>}
          </div>
        );
      })}

      {noteText !== "" && (
        /* Full width, like `.details` below it and unlike the identity cell
           beside it: that column is 118px on mobile and a two-word marker
           would stack three lines deep inside it. */
        <Text as="p" className={styles.note} variant="label">
          {noteText}
        </Text>
      )}

      {showDetails && (
        <ChannelRowDetails
          legalNote={rsnLegalNote(channel.tier, tierLegalNotes, defaultLegalNote) || undefined}
          legalTier={tierNoteMarker(channel.tier, tierLegalNotes)}
          rowCopy={rowCopy}
          teams={teams}
        />
      )}
    </div>
  );
}
export const ChannelRow = withSafeRender(ChannelRowImpl, "ChannelRow");

export default ChannelRow;
